import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Pagination,
  Stack,
} from "@mui/material";
import moment from "moment";
import axios from "axios";

// Services, Web3 & Constants
import giftcardcinstance from "../../../Service/giftcardcinstance";
import web3 from "../../../Service/web3";
import NavBar from "../../../Components/NavBar";
import { Path, DEFAULT_TOKEN_SYMBOL } from "../../../../static/Constants";
import { colors, borderRadiusPx } from "../../../../theme";
import { CommonBreadcrumbs, toast } from "../../../Components/ui";

// Subcomponents
import {
  GiftCardsHero,
  GiftCardsFilters,
  PublicGiftCard,
  GiftCardsTable,
  GiftCardsEmptyState,
  GiftCardsHeroSkeleton,
  GiftCardsGridSkeleton,
} from "./components";

import "./AllGiftCardsStyle.css";

const withTimeout = (promise, ms = 6000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const ITEMS_PER_PAGE = 9;

const AllGiftCards = () => {
  const apiUrl = process.env.REACT_APP_API_KEY || "https://testapi.autobetlottery.com/api";

  // Cached state for instant zero-latency rendering
  const [giftcardList, setGiftCardList] = useState(() => {
    try {
      const saved = localStorage.getItem("all_giftcards");
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch {}
    return [];
  });

  const [giftcardLogoData, setGiftcardLogoData] = useState(() => {
    try {
      const saved = localStorage.getItem("giftcard_logos");
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch {}
    return [];
  });

  const [loading, setLoading] = useState(() => {
    try {
      const saved = localStorage.getItem("all_giftcards");
      if (saved) {
        const parsed = JSON.parse(saved);
        return !Array.isArray(parsed) || parsed.length === 0;
      }
    } catch {}
    return true;
  });

  const [refreshing, setRefreshing] = useState(false);

  // Filters, Sort, View & Pagination State
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);

  /**
   * Fetch On-Chain Gift Cards & Logos with Robust Error & Timeout Guards
   */
  const fetchGiftCards = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setGiftCardList((current) => {
          if (!current || current.length === 0) {
            setLoading(true);
          }
          return current;
        });
      }

      // Hard watchdog timer so page NEVER gets stuck on loading spinner
      const watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7000);

      try {
        // 1. Fetch Logos in parallel
        axios
          .get(`${apiUrl}/giftcardlogos/getAllGiftcardLogos`, { timeout: 5000 })
          .then((response) => {
            if (response.data && response.data.result) {
              setGiftcardLogoData(response.data.result);
              try {
                localStorage.setItem(
                  "giftcard_logos",
                  JSON.stringify(response.data.result)
                );
              } catch {}
            }
          })
          .catch((err) => console.warn("[GiftCards] Logo fetch error:", err));

        // 2. Fetch On-Chain Gift Card Count
        let totalCards = 0;
        try {
          const countRes = await withTimeout(
            giftcardcinstance.methods.giftCardId().call(),
            5000
          );
          totalCards = countRes !== undefined && countRes !== null ? Number(countRes) : 0;
        } catch (countErr) {
          console.warn("[GiftCards] giftCardId count error:", countErr);
        }

        const maxIndex = Math.max(0, isNaN(totalCards) ? 0 : totalCards);

        // 3. Fetch each card details concurrently (always querying index 0 up to maxIndex)
        const fetchPromises = [];
        for (let i = 0; i <= maxIndex; i++) {
          fetchPromises.push(
            withTimeout(giftcardcinstance.methods.giftCards(i).call(), 5000)
              .then((res) => {
                if (!res || !res.creator || res.creator === "0x0000000000000000000000000000000000000000") {
                  return null;
                }
                const onChainId = res.giftCardId !== undefined && res.giftCardId !== "" ? res.giftCardId : i;
                return { ...res, giftCardId: onChainId };
              })
              .catch(() => null)
          );
        }

        const results = await Promise.all(fetchPromises);
        const currentDT = moment().unix();

        // 4. Filter active unsold cards that haven't expired yet
        const activeCards = results.filter((item) => {
          if (!item) return false;
          const isSold = item.isSold === true || item.isSold === "true" || item.isSold === 1;
          const expDate = Number(item.expDate || 0);
          return !isSold && (expDate === 0 || expDate > currentDT);
        });

        setGiftCardList(activeCards);
        try {
          localStorage.setItem("all_giftcards", JSON.stringify(activeCards));
        } catch (storageErr) {
          console.warn("[GiftCards] LocalStorage write error:", storageErr);
        }
      } catch (err) {
        console.error("[GiftCards] Fetch error:", err);
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiUrl]
  );

  useEffect(() => {
    fetchGiftCards();
  }, [fetchGiftCards]);

  // Tab counts
  const countsByTab = useMemo(() => {
    const total = giftcardList.length;
    const donation = giftcardList.filter(
      (item) => item.isDonation === true || item.isDonation === "true" || item.isDonation === 1
    ).length;
    const sale = total - donation;

    return {
      all: total,
      donation,
      sale,
    };
  }, [giftcardList]);

  // Total Token Value Calculation
  const totalTokenValue = useMemo(() => {
    if (!giftcardList || giftcardList.length === 0) return "0";
    try {
      const sumWei = giftcardList.reduce((acc, item) => {
        const val = item.BETValue || 0;
        return acc + Number(web3.utils.fromWei(`${val}`, "ether") || 0);
      }, 0);
      return sumWei.toLocaleString("en-US", { maximumFractionDigits: 2 });
    } catch {
      return "0";
    }
  }, [giftcardList]);

  // Filtered & Sorted Cards
  const filteredCards = useMemo(() => {
    let result = [...giftcardList];

    // 1. Tab Categorization Filter
    if (activeTab === "donation") {
      result = result.filter(
        (item) => item.isDonation === true || item.isDonation === "true" || item.isDonation === 1
      );
    } else if (activeTab === "sale") {
      result = result.filter(
        (item) => !(item.isDonation === true || item.isDonation === "true" || item.isDonation === 1)
      );
    }

    // 2. Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const idStr = String(item.giftCardId !== undefined && item.giftCardId !== null ? item.giftCardId : "");
        const idMatch = idStr.includes(q);
        const nameMatch = (item.tokenName || "").toLowerCase().includes(q);
        const descMatch = (item.Desc || "").toLowerCase().includes(q);
        const creatorMatch = (item.creator || "").toLowerCase().includes(q);
        return idMatch || nameMatch || descMatch || creatorMatch;
      });
    }

    // 3. Sorting
    result.sort((a, b) => {
      const idA = Number(a.giftCardId !== undefined && a.giftCardId !== null ? a.giftCardId : 0);
      const idB = Number(b.giftCardId !== undefined && b.giftCardId !== null ? b.giftCardId : 0);

      if (sortBy === "newest") return idB - idA;
      if (sortBy === "oldest") return idA - idB;

      if (sortBy === "valueHigh") {
        const valA = Number(web3.utils.fromWei(`${a.BETValue || 0}`, "ether") || 0);
        const valB = Number(web3.utils.fromWei(`${b.BETValue || 0}`, "ether") || 0);
        return valB - valA || idB - idA;
      }

      if (sortBy === "priceLow") {
        const priceA = Number(web3.utils.fromWei(`${a.ETHPrice || 0}`, "ether") || 0);
        const priceB = Number(web3.utils.fromWei(`${b.ETHPrice || 0}`, "ether") || 0);
        return priceA - priceB || idB - idA;
      }

      if (sortBy === "expiringSoon") {
        const expA = Number(a.expDate || 0);
        const expB = Number(b.expDate || 0);
        return expA - expB || idB - idA;
      }

      return 0;
    });

    return result;
  }, [giftcardList, activeTab, searchQuery, sortBy]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredCards.length / ITEMS_PER_PAGE);
  const paginatedCards = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredCards.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCards, page]);

  /**
   * Action Handlers
   */
  const handleShare = (item) => {
    if (!item) return;
    const cardId = item.giftCardId !== undefined ? item.giftCardId : item.id;
    const shareUrl = `${window?.location?.origin}/giftcard/${cardId}`;
    try {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable gift card link copied to clipboard!", {
        position: "top-right",
      });
    } catch (err) {
      console.error("Could not copy link:", err);
    }
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setActiveTab("all");
    setSortBy("newest");
    setPage(1);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(1);
  };

  return (
    <Box className="giftcards-canvas" sx={{ bgcolor: colors.dashboard.canvas, pb: 8 }}>
      <NavBar />

      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: "96px", sm: "108px", md: "116px" },
          pb: 6,
          flexGrow: 1,
        }}
      >
        {/* Breadcrumb Navigation */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "All Gift Cards" },
          ]}
        />

        {/* Hero Banner with Live On-Chain Metrics */}
        {loading ? (
          <GiftCardsHeroSkeleton />
        ) : (
          <GiftCardsHero
            totalCardsCount={giftcardList.length}
            donationCount={countsByTab.donation}
            saleCount={countsByTab.sale}
            totalTokenValue={totalTokenValue}
            tokenTicker={giftcardList[0]?.tokenName || DEFAULT_TOKEN_SYMBOL}
          />
        )}

        {/* Search, Filter Tabs & View Controls */}
        <GiftCardsFilters
          activeTab={activeTab}
          onTabChange={handleTabChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          sortBy={sortBy}
          onSortChange={setSortBy}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          countsByTab={countsByTab}
          onRefresh={() => fetchGiftCards(true)}
          refreshing={refreshing}
        />

        {/* Cards Content Section */}
        {loading ? (
          <GiftCardsGridSkeleton count={6} viewMode={viewMode} />
        ) : paginatedCards.length > 0 ? (
          <>
            {viewMode === "table" ? (
              <GiftCardsTable
                cards={paginatedCards}
                giftcardLogo={giftcardLogoData}
                onShare={handleShare}
              />
            ) : (
              <Grid container spacing={3}>
                {paginatedCards.map((card, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={card.giftCardId !== undefined && card.giftCardId !== null ? `giftcard-${card.giftCardId}` : `giftcard-idx-${index}`}
                  >
                    <PublicGiftCard
                      item={card}
                      giftcardLogo={giftcardLogoData}
                      viewMode="grid"
                      onShare={handleShare}
                    />
                  </Grid>
                ))}
              </Grid>
            )}


            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 5, mb: 2 }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => {
                    setPage(value);
                    window.scrollTo({ top: 320, behavior: "smooth" });
                  }}
                  color="primary"
                  shape="rounded"
                  size="medium"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 600,
                      borderRadius: borderRadiusPx.md,
                    },
                    "& .Mui-selected": {
                      bgcolor: `${colors.primary.main} !important`,
                      color: `${colors.neutral.white} !important`,
                    },
                  }}
                />
              </Stack>
            )}
          </>
        ) : (
          <GiftCardsEmptyState
            hasActiveFilters={Boolean(searchQuery.trim() || activeTab !== "all")}
            onResetFilters={handleResetFilters}
          />
        )}
      </Container>
    </Box>
  );
};

export default AllGiftCards;
